/**
 * English messages of the `#/dev/effects` comparer (W3-04 effects-fidelity; Appendix C H5): a developer and
 * verification page that compares Phiedit's preview of an extra.json effect with screenshots from Phira.
 */

import { catalogOf, defineMessages } from '../../../../i18n/define';

export const m = defineMessages('effectsDev', {
  title: 'Effects comparer',
  intro:
    'Compare the preview of an extra.json effect with Phira. Download the capture kit, play it in Phira, take one screenshot before 0:16 (effect off) and one after (effect on), then load both screenshots here.',
  noWebgl: 'WebGL 2 is not available in this browser, so effects cannot be drawn here.',

  effect: 'Effect',
  probe: 'UV orientation probe (custom shader)',
  probeDesc:
    'Tints the picture red towards the right and green towards the top. If Phira puts uv (0, 0) at the bottom-left like the preview, the top-left corner turns green, the bottom-right turns red, and the bottom-left gets no red or green tint (it only gets darker).',
  probeAmount: 'Tint amount',
  fidelity: 'Preview status: {status}',

  values: 'Values',
  resetValues: 'Reset to defaults',
  alpha: 'Alpha',
  time: 'Time (s)',
  timeHint: 'Chart seconds given to the shader as time. Only Glitch and custom shaders use it.',
  glitchTimeHint:
    'Glitch changes its bands several times per second (Changes per second), so it matches Phira only at the chart time of the Phira screenshot. To compare pixels, enter that time here: the filled share of the progress bar along the top of the Phira screenshot, times {song} s (the length of the song in the kit). Otherwise compare Glitch by eye: band height, shift and colour split.',
  scope: 'Applies to',
  scopeGlobal: 'Whole screen (global)',
  scopeChart: 'Chart area, 16:9 (not global)',
  scopeChartHud:
    'Phira draws its HUD (score, progress bar, pause button) after a chart-area effect, so the effect never touches the HUD there. Here it also runs over the HUD of the Before screenshot, so the HUD always shows as different. Use Whole screen for a clean comparison.',

  images: 'Screenshots',
  before: 'Before (effect off)',
  beforeHint: 'The picture the effect reads. The test card is used until you load a screenshot.',
  phira: 'Phira (effect on)',
  phiraHint: 'Phira with the effect on, at the same window size as Before.',
  chooseImage: 'Choose image',
  dropHint: 'or drop an image here',
  useTestCard: 'Use test card',
  removeImage: 'Remove',
  testCard: 'Test card',
  imageSize: '{name}, {w} x {h}',
  imageFailed: 'This file could not be read as an image.',

  view: 'View',
  viewSide: 'Side by side',
  viewBlink: 'Blink',
  viewDiff: 'Difference',
  paneOurs: 'Phiedit',
  panePhira: 'Phira',
  paneDiff: 'Difference',
  blinkShowing: 'Showing: {name}',
  noCapture: 'Load a Phira screenshot to compare.',
  sizeMismatch:
    'The Phira screenshot is {w} x {h} and Before is {bw} x {bh}, so it was scaled to compare. Take both screenshots at the same window size.',

  stats: 'Difference',
  statMean: 'Mean difference',
  statPsnr: 'PSNR',
  statOver: 'Pixels off by more than {threshold}',
  statMax: 'Largest difference',
  psnrValue: '{value, number, 0.0} dB',
  identical: 'Identical',
  percentValue: '{value, number, 0.00} %',
  levelValue: '{value, number, 0.0} of 255',

  shaderError: 'The shader failed to compile: {log}',
  notPreviewed: 'The preview has no shader for this effect.',
  droppedVars: 'Ignored because the value does not fit the uniform type: {names}',

  kit: 'Capture kit',
  kitHint:
    'A Phira package with the test card and this effect at these values. The effect is off until 0:16 and on from 0:16 to 0:40.',
  downloadKit: 'Download capture kit (.pez)',
  kitName: 'Phiedit effect check: {effect}',
  kitTip: 'Take one screenshot before 0:16 (effect off) and one after 0:16 (effect on).',
  saveRender: 'Save Phiedit render (.png)',

  record: 'Record the result',
  tester: 'Tester',
  phiraVersion: 'Phira version and device',
  notes: 'Notes',
  matches: 'Matches Phira',
  differs: 'Differs from Phira',
  recordHint:
    'Store the record and both screenshots in docs/verification/effects/. To sign off a matching built-in, add the entry below to SIGNED_OFF in src/render/preview/effects/builtin/signOff.ts — only after CR-2 (the effectBadges "verified" branch) has landed: its Approximate badge then disappears. Until CR-2 lands, a signed-off built-in would show "Effect not previewed".',
  copy: 'Copy',
  copied: 'Copied',
  downloadJson: 'Download JSON',
  signOffEntry: 'SIGNED_OFF entry',
  entryIncomplete:
    'A SIGNED_OFF entry needs a Phira screenshot, a tester and the Phira version. Missing: {missing}.',
  entryNeedCapture: 'Phira screenshot',
  entryNeedTester: 'Tester',
  entryNeedPhiraVersion: 'Phira version',
});

export default catalogOf(m);
